import { addDays, subDays } from 'date-fns';
import type { TFunction } from 'i18next';
import { DateRangePicker } from 'rsuite';
import * as amplitude from '@amplitude/analytics-browser';

import type { TCurrentTime } from 'libs/design-system/src/types/time_picker';
import type { IPreDefinedRange } from 'libs/design-system/src/types/date_range_picker';

import type { TAppliedRanges } from '@app/dashboard/types/innerLayout/date-ranges';
import type { ICreateCampaign } from '@app/dashboard/types/innerLayout/reach/modals/create-modal';
import {
  REACH_PAGE_EVENTS,
  REACH_PAGE_EVENTS_PROPERTIES,
} from '@app/dashboard/constants/tracking_events/reach';
import {
  CREATE_CAMPAIGN_FIELDS,
  MESSAGE_REGEX,
  REQUIRED_REGEX,
} from '@app/dashboard/constants/reach/modals/create-campaign';
import { getDefaultDates } from '@app/dashboard/utils/dateRangePicker';
import { formatToISODate } from '@app/dashboard/utils/dateFormats';

export const getValidations = (t: TFunction) => ({
  [CREATE_CAMPAIGN_FIELDS.TITLE]: [
    {
      pattern: REQUIRED_REGEX,
      error_msg: t('errors.required'),
    },
    {
      pattern: MESSAGE_REGEX,
      error_msg: t(
        'innerLayout.reach.campaigns.createCampaign.validations.invalidTitle'
      ),
    },
  ],
  [CREATE_CAMPAIGN_FIELDS.DESCRIPTION]: [
    {
      pattern: REQUIRED_REGEX,
      error_msg: t('errors.required'),
    },
    {
      pattern: MESSAGE_REGEX,
      error_msg: t(
        'innerLayout.reach.campaigns.createCampaign.validations.invalidDescription'
      ),
    },
  ],
  [CREATE_CAMPAIGN_FIELDS.CONTENT]: [
    {
      pattern: REQUIRED_REGEX,
      error_msg: t('errors.required'),
    },
    {
      pattern: MESSAGE_REGEX,
      error_msg: t(
        'innerLayout.reach.campaigns.createCampaign.validations.invalidContent'
      ),
    },
  ],
  [CREATE_CAMPAIGN_FIELDS.DATE]: [
    {
      pattern: REQUIRED_REGEX,
      error_msg: t('errors.required'),
    },
  ],
  [CREATE_CAMPAIGN_FIELDS.TIME]: [
    {
      pattern: REQUIRED_REGEX,
      error_msg: t('errors.required'),
    },
  ],
  [CREATE_CAMPAIGN_FIELDS.MOBILE_NUMBERS]: [
    {
      pattern: REQUIRED_REGEX,
      error_msg: t('errors.required'),
    },
  ],
});

export const campaignDatePresets: (t: TFunction) => IPreDefinedRange[] = (
  t
) => {
  const today = new Date();
  const tomorrow = addDays(today, 1);
  const inThreeDays = addDays(today, 3);

  const { startDate: defaultMainStartDate, endDate: defaultMainEndDate } =
    getDefaultDates();

  const { allowedRange } = DateRangePicker;

  return [
    {
      index: 1,
      name: 'today',
      disabled: false,
      label: t('innerLayout.reach.campaigns.createCampaign.datePresets.today'),
      selectionRange: [today, today],
      disabledRange: allowedRange(today, today),
    },
    {
      index: 2,
      name: 'tomorrow',
      disabled: false,
      label: t(
        'innerLayout.reach.campaigns.createCampaign.datePresets.tomorrow'
      ),
      selectionRange: [tomorrow, tomorrow],
      disabledRange: allowedRange(tomorrow, tomorrow),
    },
    {
      index: 3,
      name: 'dayAfterTomorrow',
      disabled: false,
      label: t(
        'innerLayout.reach.campaigns.createCampaign.datePresets.dayAfterTomorrow'
      ),
      selectionRange: [inThreeDays, inThreeDays],
      disabledRange: allowedRange(inThreeDays, inThreeDays),
    },
    {
      index: 4,
      name: 'custom',
      disabled: false,
      label: t(`innerLayout.reach.campaigns.createCampaign.datePresets.custom`),
      selectionRange: [today, addDays(today, 365 * 10)],
      disabledRange: (date: Date) => {
        return date < subDays(today, 1);
      },
      default: true,
    },
  ];
};

export const hasArabic = (value: string) => /[\u0600-\u06FF]/.test(value);
export const hasEmoji = (value: string) => /\p{Extended_Pictographic}/u.test(value);
export const isEnglishOnly = (value: string) =>
  /^[A-Za-z0-9\s.,'~!@#$%^&*()_\-+={}[\]|\\<>?":;]*$/.test(value);

export const handleAmplitudeTrackingOnCreateCampaign = ({
  campaignData,
  success,
  errorData,
  fields,
  senderName,
}: {
  campaignData?: ICreateCampaign;
  success: boolean;
  errorData?: any;
  fields: any;
  senderName: string;
}) => {
  let properties = {};

  if (success && campaignData) {
    const date = fields[CREATE_CAMPAIGN_FIELDS.DATE].value as TAppliedRanges;
    const time = fields[CREATE_CAMPAIGN_FIELDS.TIME].value as TCurrentTime;

    const formattedTime = `${time.hour}:${time.minute} ${time.ampm}`;

    const campaignStartDate =
      formatToISODate(date?.start as Date, 'dd MMM yyyy') +
      ', ' +
      formattedTime;

    const props =
      REACH_PAGE_EVENTS_PROPERTIES.CAMPAIGNS_PAGE.CAMPAIGN_CREATED_SUCCESS;

    return {
      [props.SENDER_NAME]: senderName,
      [props.CAMPAIGN_TITLE]: campaignData.title,
      [props.UPLOAD_CAMPAIGN_RECEPIENT]: campaignData.mobile_numbers,
      [props.DESCRIPTION]: campaignData.description,
      [props.SMS_CONTENT]: campaignData.content,
      [props.CAMPAIGN_START_DATE]: campaignStartDate,
      [props.CAMPAIGN_TIME]: formattedTime,
    };
  }

  if (!success && errorData) {
    properties = {
      [REACH_PAGE_EVENTS_PROPERTIES.SHARED.ERROR_MESSAGE]: errorData?.message,
      ...(errorData?.data || {}),
    };
  }

  amplitude.track(
    success
      ? REACH_PAGE_EVENTS.CAMPAIGNS_PAGE.CAMPAIGN_CREATED_SUCCESS
      : REACH_PAGE_EVENTS.CAMPAIGNS_PAGE.CAMPAIGN_CREATED_ERROR,
    properties
  );
};
